<template>
  <div class="container-fluid mt-5">
    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-3x text-primary"></i>
    </div>

    <div v-else>
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-10">
              <h3 class="mb-0 view-header-buttons">
                {{ weeklyReport.establishment.company_name }}

                <span
                  class="badge ml-2"
                  :class="weeklyReport.status === 'VALIDATED' ? 'badge-success' : 'badge-warning'"
                >
                  {{ weeklyReport.status === 'VALIDATED'
                    ? $t("WEEKLY_REPORTS.REPORT_VALIDATED")
                    : $t("WEEKLY_REPORTS.REPORT_DRAFT") }}
                </span>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_EDIT_WEEKLY_REPORTS) &&
                    weeklyReport.status === 'DRAFT'
                  "
                  type="primary"
                  icon
                  size="sm"
                  :disabled="updating"
                  @click="editWeeklyReport()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_DELETE_WEEKLY_REPORTS) &&
                    weeklyReport.status === 'DRAFT'
                  "
                  type="danger"
                  icon
                  size="sm"
                  :disabled="updating"
                  @click="deleteWeeklyReport()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_EDIT_WEEKLY_REPORTS) &&
                    weeklyReport.status === 'DRAFT'
                  "
                  type="info"
                  icon
                  size="sm"
                  :disabled="refreshing"
                  @click="refreshWeeklyReport()"
                  v-tooltip="$t('WEEKLY_REPORTS.REFRESH_CALCULATIONS')"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_EDIT_WEEKLY_REPORTS) &&
                    weeklyReport.status === 'DRAFT'
                  "
                  type="success"
                  size="sm"
                  :disabled="updating || validating"
                  @click="validateWeeklyReport()"
                >
                  <span class="btn-inner--icon">
                    <i class="ni ni-check-bold"></i>
                  </span>
                  {{ $t("WEEKLY_REPORTS.VALIDATE_REPORT") }}
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_REOPEN_WEEKLY_REPORTS) &&
                    weeklyReport.status === 'VALIDATED'
                  "
                  type="warning"
                  size="sm"
                  :disabled="updating"
                  @click="revertToDraft()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-undo"></i>
                  </span>
                  {{ $t("WEEKLY_REPORTS.REVERT_TO_DRAFT") }}
                </base-button>

                <!-- Save indicators come last: appearing on blur, they used to push
                     the buttons onto the next line under the pointer, and the click
                     on « Valider » right after typing the wages was lost. -->
                <span v-if="updating" class="ml-2">
                  <i class="fas fa-spinner fa-spin text-warning"></i>
                  <small class="text-muted ml-1"
                    >{{ $t("WEEKLY_REPORTS.SAVING") }}...</small>
                </span>
                <span v-else-if="hasPendingChanges" class="ml-2">
                  <i class="fas fa-clock text-info"></i>
                  <small class="text-muted ml-1">
                    {{ $t("WEEKLY_REPORTS.UNSAVED_CHANGES") }}
                  </small>
                </span>
              </h3>
            </div>
            <div class="col-2 text-right">
              <base-button
                @click="goBack()"
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="updating"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>
            </div>
          </div>
        </template>

        <!-- Add summary cards for key financial data -->
        <div class="container-fluid py-3">
          <div class="row">
            <!-- Total Sales Card -->
            <div class="col-xl-3 col-md-6">
              <div class="card card-stats h-100">
                <div class="card-body">
                  <div class="row">
                    <div class="col">
                      <h5 class="card-title text-uppercase text-muted mb-0">
                        {{ $t("WEEKLY_REPORTS.TOTAL_SALES") }}
                      </h5>
                      <el-tooltip :content="wrFormulaTotalSales(weeklyReport)" placement="top">
                        <span class="h2 font-weight-bold mb-0 wr-kpi-value">
                          {{ $formatCurrency(weeklyReport.total_sales || 0) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                    <div class="col-auto">
                      <div
                        class="icon icon-shape bg-gradient-green text-white rounded-circle shadow"
                      >
                        <i class="ni ni-money-coins"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Total Purchases Card -->
            <div class="col-xl-3 col-md-6">
              <div class="card card-stats h-100">
                <div class="card-body">
                  <div class="row">
                    <div class="col">
                      <h5 class="card-title text-uppercase text-muted mb-0">
                        {{ $t("WEEKLY_REPORTS.TOTAL_PURCHASES") }}
                      </h5>
                      <el-tooltip :content="wrFormulaTotalPurchases(weeklyReport)" placement="top">
                        <span class="h2 font-weight-bold mb-0 wr-kpi-value">
                          {{ $formatCurrency(weeklyReport.total_purchases || 0) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                    <div class="col-auto">
                      <div
                        class="icon icon-shape bg-gradient-danger text-white rounded-circle shadow"
                      >
                        <i class="ni ni-cart"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Total Costs Card -->
            <div class="col-xl-3 col-md-6">
              <div class="card card-stats h-100">
                <div class="card-body">
                  <div class="row">
                    <div class="col">
                      <h5 class="card-title text-uppercase text-muted mb-0">
                        {{ $t("WEEKLY_REPORTS.TOTAL_COSTS") }}
                      </h5>
                      <el-tooltip :content="wrFormulaTotalCosts(weeklyReport)" placement="top">
                        <span class="h2 font-weight-bold mb-0 wr-kpi-value">
                          {{ $formatCurrency(weeklyReport.total_costs || 0) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                    <div class="col-auto">
                      <div
                        class="icon icon-shape bg-gradient-warning text-white rounded-circle shadow"
                      >
                        <i class="ni ni-credit-card"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Net Profit Card -->
            <div class="col-xl-3 col-md-6">
              <div class="card card-stats h-100">
                <div class="card-body">
                  <div class="row">
                    <div class="col">
                      <h5 class="card-title text-uppercase text-muted mb-0">
                        {{ $t("WEEKLY_REPORTS.NET_PROFIT") }}
                      </h5>
                      <el-tooltip :content="wrFormulaNetProfit(weeklyReport)" placement="top">
                        <span class="h2 font-weight-bold mb-0 wr-kpi-value">
                          {{ $formatCurrency(weeklyReport.net_profit || 0) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                    <div class="col-auto">
                      <div
                        class="icon icon-shape bg-gradient-info text-white rounded-circle shadow"
                      >
                        <i class="ni ni-chart-bar-32"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Target Ratio Analysis Section -->
        <div class="container-fluid py-3">
          <div class="row">
            <!-- Wages Ratio Card -->
            <div class="col-md-3 mb-3">
              <div class="card h-100 shadow-sm target-ratio-card">
                <div
                  class="card-header text-white"
                  :class="getTargetStatusClass(wagesRatioDifference)"
                >
                  <h4 class="mb-1">{{ $t("WEEKLY_REPORTS.WAGES") }}</h4>
                  <el-tooltip
                    :content="$t('WEEKLY_REPORTS.SRC_WAGES')"
                    placement="top"
                  >
                    <div class="h6 mb-0 font-weight-bold text-white wr-kpi-value">
                      {{ $formatCurrency(weeklyReport.wages_with_taxes || 0) }}
                    </div>
                  </el-tooltip>
                </div>
                <div class="card-body">
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.RESULT") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="wrFormulaWagesRatio(weeklyReport)"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatPercentage(weeklyReport.wages_ratio) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.TARGET") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_TARGET')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatPercentage(weeklyReport.target_wages) }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.DIFFERENCE") }}:
                    </div>
                    <div
                      class="col-6 font-weight-bold"
                      :class="getDifferenceClass(wagesRatioDifference)"
                    >
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_DIFFERENCE')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ formatPercentageDifference(wagesRatioDifference) }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="mt-2">
                    <div
                      class="badge"
                      :class="getTargetStatusClass(wagesRatioDifference)"
                    >
                      {{ getTargetStatusText(wagesRatioDifference) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Purchases Ratio Card -->
            <div class="col-md-3 mb-3">
              <div class="card h-100 shadow-sm target-ratio-card">
                <div
                  class="card-header text-dark"
                  :class="getTargetStatusClass(purchasesRatioDifference)"
                >
                  <h4 class="mb-1">
                    {{ $t("WEEKLY_REPORTS.PURCHASES") }}
                  </h4>
                  <el-tooltip
                    :content="wrFormulaTotalPurchases(weeklyReport)"
                    placement="top"
                  >
                    <div class="h6 mb-0 font-weight-bold text-white wr-kpi-value">
                      {{ $formatCurrency(weeklyReport.total_purchases || 0) }}
                    </div>
                  </el-tooltip>
                </div>
                <div class="card-body">
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.RESULT") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="wrFormulaPurchasesRatio(weeklyReport)"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatPercentage(weeklyReport.purchases_ratio) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.TARGET") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_TARGET')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatPercentage(weeklyReport.target_purchases) }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.DIFFERENCE") }}:
                    </div>
                    <div
                      class="col-6 font-weight-bold"
                      :class="getDifferenceClass(purchasesRatioDifference)"
                    >
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_DIFFERENCE')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ formatPercentageDifference(purchasesRatioDifference) }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="mt-2">
                    <div
                      class="badge"
                      :class="getTargetStatusClass(purchasesRatioDifference)"
                    >
                      {{ getTargetStatusText(purchasesRatioDifference) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Purchases Consumables Ratio Card -->
            <div class="col-md-3 mb-3">
              <div class="card h-100 shadow-sm target-ratio-card">
                <div
                  class="card-header text-dark"
                  :class="getTargetStatusClass(consumablesRatioDifference)"
                >
                  <h4 class="mb-1">
                    {{ $t("WEEKLY_REPORTS.PURCHASES_CONSUMABLES") }}
                  </h4>
                  <el-tooltip
                    :content="$t('WEEKLY_REPORTS.SRC_CONSUMABLES_PURCHASES')"
                    placement="top"
                  >
                    <div class="h6 mb-0 font-weight-bold text-white wr-kpi-value">
                      {{
                        $formatCurrency(
                          weeklyReport.consumables_purchases_total || 0
                        )
                      }}
                    </div>
                  </el-tooltip>
                </div>
                <div class="card-body">
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.RESULT") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="wrFormulaConsumablesRatio(weeklyReport)"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatPercentage(weeklyReport.consumables_ratio) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.TARGET") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_TARGET')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatPercentage(weeklyReport.target_consumables) }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.DIFFERENCE") }}:
                    </div>
                    <div
                      class="col-6 font-weight-bold"
                      :class="getDifferenceClass(consumablesRatioDifference)"
                    >
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_DIFFERENCE')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{
                            formatPercentageDifference(consumablesRatioDifference)
                          }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="mt-2">
                    <div
                      class="badge"
                      :class="getTargetStatusClass(consumablesRatioDifference)"
                    >
                      {{ getTargetStatusText(consumablesRatioDifference) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Net Profit Card -->
            <div class="col-md-3 mb-3">
              <div class="card h-100 shadow-sm target-ratio-card">
                <div
                  class="card-header text-white"
                  :class="getTargetStatusClass(netProfitDifference, true)"
                >
                  <h4 class="mb-1">
                    {{ $t("WEEKLY_REPORTS.NET_PROFIT") }}
                  </h4>
                  <el-tooltip
                    :content="wrFormulaNetProfit(weeklyReport)"
                    placement="top"
                  >
                    <div class="h6 mb-0 font-weight-bold text-white wr-kpi-value">
                      {{ $formatCurrency(weeklyReport.net_profit || 0) }}
                    </div>
                  </el-tooltip>
                </div>
                <div class="card-body">
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.RESULT") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="wrFormulaNetProfit(weeklyReport)"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatCurrency(weeklyReport.net_profit || 0) }}
                          <i class="fas fa-info-circle wr-kpi-icon"></i>
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.TARGET") }}:
                    </div>
                    <div class="col-6 font-weight-bold">
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_TARGET')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ $formatCurrency(weeklyReport.target_net_profit || 0) }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6 text-muted">
                      {{ $t("WEEKLY_REPORTS.DIFFERENCE") }}:
                    </div>
                    <div
                      class="col-6 font-weight-bold"
                      :class="getDifferenceClass(netProfitDifference, true)"
                    >
                      <el-tooltip
                        :content="$t('WEEKLY_REPORTS.SRC_NET_PROFIT_DIFFERENCE')"
                        placement="top"
                      >
                        <span class="wr-kpi-value">
                          {{ formatCurrencyDifference(netProfitDifference) }}
                        </span>
                      </el-tooltip>
                    </div>
                  </div>
                  <div class="mt-2">
                    <div
                      class="badge"
                      :class="getTargetStatusClass(netProfitDifference, true)"
                    >
                      {{ getTargetStatusText(netProfitDifference, true) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div
            class="col-12 justify-content-center justify-content-sm-between flex-wrap"
          >
            <tabs
              fill
              class="flex-column flex-md-row"
              tabNavWrapperClasses="nav-wrapper"
              tabNavClasses="nav nav-pills nav-fill"
              value="dailySales"
            >
              <card shadow>
                <tab-pane title="global" id="1" :active="true">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GLOBAL") }}
                  </span>
                  <weekly-report-view-global
                    :weeklyReport="weeklyReport"
                    @weeklyReportUpdated="get"
                  />
                </tab-pane>

                <tab-pane title="dailySales" id="2">
                  <span slot="title">
                    <i class="fas fa-calendar-week" />
                    {{ $t("WEEKLY_REPORTS.DAILY_SALES") }}
                  </span>
                  <weekly-report-view-details
                    :weeklyReport="weeklyReport"
                    @update:weeklyReport="updateWeeklyReport"
                  />
                </tab-pane>

                <!-- New tab for Cash Register Readings -->
                <tab-pane title="cashRegisterReadings" id="3">
                  <span slot="title">
                    <i class="fa fa-cash-register" />
                    {{ $t("WEEKLY_REPORTS.CASH_REGISTER_READINGS") }}
                  </span>
                  <weekly-report-view-cash-register-readings
                    :weeklyReport="weeklyReport"
                  />
                </tab-pane>

                <!-- New tab for sales orders -->
                <tab-pane title="salesInvoices" id="4">
                  <span slot="title">
                    <i class="fa fa-file-invoice-dollar" />
                    {{ $t("WEEKLY_REPORTS.SALES_ORDERS") }}
                  </span>
                  <weekly-report-view-sales-orders
                    :weeklyReport="weeklyReport"
                    :canEdit="true"
                    @update:weeklyReport="updateWeeklyReport"
                  />
                </tab-pane>

                <!-- New tab for purchases Orders -->
                <tab-pane title="purchasesInvoices" id="5">
                  <span slot="title">
                    <i class="fa fa-shopping-cart" />
                    {{ $t("WEEKLY_REPORTS.PURCHASES_ORDERS") }}
                  </span>
                  <weekly-report-view-purchases-orders
                    :weeklyReport="weeklyReport"
                    :canEdit="true"
                    @update:weeklyReport="updateWeeklyReport"
                  />
                </tab-pane>

                <tab-pane title="transfers" id="6">
                  <span slot="title">
                    <i class="fa fa-exchange-alt" />
                    {{ $t("COMMON.TRANSFERS") }}
                  </span>
                  <weekly-report-view-transfers
                    :weeklyReport="weeklyReport"
                    @update:weeklyReport="updateWeeklyReport"
                  />
                </tab-pane>

                <tab-pane title="internal-invoices" id="888">
                  <span slot="title">
                    <i class="fa fa-file-invoice-dollar" />
                    {{ $t("COMMON.INTERNAL_INVOICES") }}
                  </span>
                  <weekly-report-view-internal-invoices
                    :weeklyReport="weeklyReport"
                    @update:weeklyReport="updateWeeklyReport"
                  />
                </tab-pane>

                <tab-pane
                  title="logs"
                  id="999"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOGS)"
                >
                  <span slot="title">
                    <i class="fa fa-file" />
                    {{ $t("COMMON.LOGS") }}
                  </span>
                  <weekly-report-view-logs :weeklyReport="weeklyReport" />
                </tab-pane>
              </card>
            </tabs>
          </div>
        </div>
      </card>
    </div>

    <modal :show="openItemsModalVisible" @close="closeOpenItemsModal" modal-classes="modal-lg">
      <template slot="header">
        <h5 class="modal-title">
          {{
            isWagesMissing
              ? $t("WEEKLY_REPORTS.VALIDATE_BLOCKED_TITLE")
              : hasOpenItems
              ? $t("WEEKLY_REPORTS.VALIDATE_WARNING_TITLE")
              : $t("WEEKLY_REPORTS.CONFIRM_VALIDATE_TITLE")
          }}
        </h5>
      </template>

      <div class="p-3">
        <div v-if="isWagesMissing" class="alert alert-danger">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          {{ $t("WEEKLY_REPORTS.WAGES_REQUIRED") }}
        </div>
        <template v-if="hasOpenItems">
          <p>{{ $t("WEEKLY_REPORTS.OPEN_ITEMS_WARNING") }}</p>
          <div
            v-for="section in openItemsSections"
            :key="section.routeName"
            class="mb-3"
          >
            <p class="mb-1 font-weight-bold">{{ section.title }}</p>
            <ul class="pl-4 mb-0">
              <li v-for="item in section.items" :key="item.id">
                <badge :type="item.statusType" class="mr-1">
                  {{ item.statusLabel }}
                </badge>
                <a
                  v-if="item.href"
                  :href="item.href"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ item.label }}</a>
                <span v-else>{{ item.label }}</span>
                <span v-if="item.date" class="text-muted"> — {{ item.date }}</span>
              </li>
            </ul>
          </div>
          <p class="mt-3 mb-0">{{ $t("WEEKLY_REPORTS.RECOMMEND_CLOSE_ITEMS") }}</p>
        </template>
        <p v-else-if="!isWagesMissing" class="mb-0">
          {{ $t("WEEKLY_REPORTS.CONFIRM_VALIDATE_TEXT") }}
        </p>
      </div>

      <template slot="footer">
        <base-button type="secondary" @click="closeOpenItemsModal">
          {{ isWagesMissing ? $t("COMMON.CLOSE") : $t("COMMON.CANCEL") }}
        </base-button>
        <!-- Wages are mandatory: no way around it, the API refuses it too. -->
        <base-button
          v-if="!isWagesMissing"
          type="primary"
          @click="confirmValidate"
          :loading="validating"
        >
          {{
            hasOpenItems
              ? $t("WEEKLY_REPORTS.VALIDATE_ANYWAY")
              : $t("COMMON.YES")
          }}
        </base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep, debounce } from "lodash";
import swal from "sweetalert2";
import { Tabs, TabPane, Modal } from "@/components";
import { invoiceStatusesOption } from "@/constants/invoices";
import defaultWeeklyReport from "./defaultWeeklyReport";
import WeeklyReportViewGlobal from "./partials/WeeklyReportViewGlobal.vue";
import WeeklyReportViewLogs from "./partials/WeeklyReportViewLogs.vue";
import WeeklyReportViewCashRegisterReadings from "./partials/WeeklyReportViewCashRegisterReadings.vue"; // Import new component
import WeeklyReportViewPurchasesOrders from "./partials/WeeklyReportViewPurchasesOrders.vue";
import WeeklyReportViewSalesOrders from "./partials/WeeklyReportViewSalesOrders.vue";
import WeeklyReportViewTransfers from "./partials/WeeklyReportViewTransfers.vue";
import WeeklyReportViewInternalInvoices from "./partials/WeeklyReportViewInternalInvoices.vue";
import formMixin from "@/mixins/form-mixin";
import weeklyReportFormulasMixin from "@/mixins/weekly-report-formulas-mixin";
import WeeklyReportViewDetails from "./partials/WeeklyReportViewDetails.vue";
import weeklyReports from "../../../../i18n/fr/weeklyReports";

export default {
  layout: "DashboardLayout",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    Tabs,
    TabPane,
    Modal,
    WeeklyReportViewGlobal,
    WeeklyReportViewLogs,
    WeeklyReportViewCashRegisterReadings,
    WeeklyReportViewPurchasesOrders,
    WeeklyReportViewSalesOrders,
    WeeklyReportViewTransfers,
    WeeklyReportViewInternalInvoices,
    WeeklyReportViewDetails, // Register new component
  },

  mixins: [formMixin, weeklyReportFormulasMixin],

  data() {
    return {
      printModalOpened: false,
      weeklyReport: cloneDeep(defaultWeeklyReport),
      loading: false,
      updating: false,
      refreshing: false,
      validating: false,
      hasPendingChanges: false,
      openItemsModalVisible: false,
      openItemsData: null,
    };
  },

  computed: {
    wagesRatioDifference() {
      return (
        (this.weeklyReport.wages_ratio || 0) - this.weeklyReport.target_wages
      );
    },

    purchasesRatioDifference() {
      return (
        (this.weeklyReport.purchases_ratio || 0) -
        this.weeklyReport.target_purchases
      );
    },

    consumablesRatioDifference() {
      return (
        (this.weeklyReport.consumables_ratio || 0) -
        this.weeklyReport.target_consumables
      );
    },

    netProfitDifference() {
      return (
        (this.weeklyReport.net_profit || 0) -
        (this.weeklyReport.target_net_profit || 0)
      );
    },

    hasOpenItems() {
      return !!(this.openItemsData && this.openItemsData.has_open_items);
    },

    // Wages are typed by hand: 0 almost always means they were forgotten.
    isWagesMissing() {
      return !(Number(this.weeklyReport.wages) > 0);
    },

    openItemsSections() {
      const o = this.openItemsData;
      if (!o) {
        return [];
      }
      const defs = [
        { items: o.sales_orders, routeName: "View SalesOrder", countKey: "WEEKLY_REPORTS.OPEN_SALES_ORDERS_COUNT", docType: "order", statusPrefix: "SALES_ORDERS" },
        { items: o.sales_invoices, routeName: "View SalesInvoice", countKey: "WEEKLY_REPORTS.OPEN_SALES_INVOICES_COUNT", docType: "invoice" },
        { items: o.purchases_orders, routeName: "View PurchasesOrder", countKey: "WEEKLY_REPORTS.OPEN_PURCHASES_ORDERS_COUNT", docType: "order", statusPrefix: "PURCHASES_ORDERS" },
        { items: o.purchases_invoices, routeName: "View PurchasesInvoice", countKey: "WEEKLY_REPORTS.OPEN_PURCHASES_INVOICES_COUNT", docType: "invoice" },
        { items: o.transfers, routeName: "View EstablishmentTransfer", countKey: "WEEKLY_REPORTS.OPEN_TRANSFERS_COUNT", statusPrefix: "ESTABLISHMENT_TRANSFERS" },
        { items: o.internal_invoices, routeName: "View EstablishmentInternalInvoice", countKey: "WEEKLY_REPORTS.OPEN_INTERNAL_INVOICES_COUNT", statusPrefix: "ESTABLISHMENT_INTERNAL_INVOICES" },
        { items: o.cash_register_readings, routeName: "View CashRegisterReading", countKey: "WEEKLY_REPORTS.OPEN_CASH_REGISTER_READINGS_COUNT", statusPrefix: "CASH_REGISTER_READINGS" },
      ];

      return defs
        .filter((s) => (s.items || []).length > 0)
        .map((s) => ({
          routeName: s.routeName,
          title: this.$t(s.countKey, { count: s.items.length }),
          items: s.items.map((it) => ({
            id: it.id,
            label: it.label,
            date: it.date ? this.$formatDate(it.date, "ll") : "",
            href: this.resolveDocHref(s.routeName, it.id),
            ...this.openItemStatusBadge(it, s),
          })),
        }));
    },
  },

  created() {
    this.get();
    // Create debounced version of update method for API calls with longer delay
    this.debouncedUpdateWeeklyReport = debounce(this.saveWeeklyReport, 2000);
    // Create optimistic update handler for immediate UI updates
    this.optimisticUpdate = debounce(this.performOptimisticUpdate, 300);

    // Add keyboard shortcuts
    this.setupKeyboardShortcuts();
  },

  beforeDestroy() {
    // Save any pending changes before component is destroyed
    if (this.hasPendingChanges && !this.updating) {
      this.saveWeeklyReport();
    }

    // Remove keyboard event listeners
    this.removeKeyboardShortcuts();
  },

  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes before leaving
    if (this.hasPendingChanges && !this.updating) {
      // Show confirmation dialog
      this.$confirm(
        this.$t("COMMON.UNSAVED_CHANGES_WARNING"),
        this.$t("COMMON.WARNING"),
        {
          confirmButtonText: this.$t("COMMON.SAVE_AND_LEAVE"),
          cancelButtonText: this.$t("COMMON.LEAVE_WITHOUT_SAVING"),
          type: "warning",
          showCancelButton: true,
          distinguishCancelAndClose: true,
        }
      )
        .then(() => {
          // Save changes and then leave
          this.saveWeeklyReport().then(() => {
            next();
          });
        })
        .catch((action) => {
          if (action === "cancel") {
            // Leave without saving
            next();
          } else {
            // Stay on page
            next(false);
          }
        });
    } else {
      next();
    }
  },

  methods: {
    async get() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("weeklyReports/get", id);
        this.weeklyReport = this.$store.getters["weeklyReports/weeklyReport"];
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },

    async editWeeklyReport() {
      this.$router.push({
        name: "Edit WeeklyReport",
        params: { id: this.weeklyReport.id },
      });
    },

    async deleteWeeklyReport() {
      const confirmation = await swal.fire({
        title: this.$t("WEEKLY_REPORTS.DELETE_THIS_WEEKLY_REPORT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch(
            "weeklyReports/destroy",
            this.weeklyReport.id
          );
          await this.goBack();
          this.$notify({
            type: "success",
            message: this.$t("WEEKLY_REPORTS.WEEKLY_REPORT_DELETED"),
          });
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async refreshWeeklyReport() {
      this.refreshing = true;
      try {
        await this.$store.dispatch("weeklyReports/refresh", this.weeklyReport.id);
        await this.get();
        this.$notify({
          type: "success",
          message: this.$t("WEEKLY_REPORTS.CALCULATIONS_REFRESHED"),
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.refreshing = false;
      }
    },
    async validateWeeklyReport() {
      this.validating = true;
      try {
        // Save what was just typed first: confirming within the autosave delay
        // used to cancel it, and wages entered right before validating were lost.
        if (!(await this.flushPendingChanges())) {
          return;
        }
        this.openItemsData = await this.$store.dispatch(
          "weeklyReports/openItemsCheck",
          this.weeklyReport.id
        );
        this.openItemsModalVisible = true;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.validating = false;
      }
    },

    resolveDocHref(routeName, id) {
      try {
        return this.$router.resolve({ name: routeName, params: { id } }).href;
      } catch (e) {
        return "";
      }
    },

    openItemStatusBadge(item, section) {
      if (section.docType === "order") {
        // Non-draft orders display their invoicing status, like the order status badges do.
        if (item.status !== "DRAFT" && item.invoicing_status) {
          return {
            statusLabel: this.$t(`${section.statusPrefix}.INVOICING_STATUS_${item.invoicing_status}`),
            statusType: item.invoicing_status === "PARTIALLY_INVOICED" ? "primary" : "default",
          };
        }
        return {
          statusLabel: this.$t(`${section.statusPrefix}.ORDER_STATUS_${item.status}`),
          statusType: item.status === "DRAFT" ? "primary" : "default",
        };
      }
      if (section.docType === "invoice") {
        return {
          statusLabel: invoiceStatusesOption[item.status] || item.status,
          statusType: item.status === "DRAFT" ? "primary" : "default",
        };
      }
      return {
        statusLabel: this.$t(`${section.statusPrefix}.STATUS_${item.status}`),
        statusType: item.status === "DRAFT" ? "primary" : "info",
      };
    },

    closeOpenItemsModal() {
      this.openItemsModalVisible = false;
    },

    async confirmValidate() {
      this.validating = true;
      try {
        // Cancel any pending debounced saves
        if (this.debouncedUpdateWeeklyReport && this.debouncedUpdateWeeklyReport.cancel) {
          this.debouncedUpdateWeeklyReport.cancel();
        }
        this.hasPendingChanges = false;

        await this.$store.dispatch("weeklyReports/validate", this.weeklyReport.id);
        this.weeklyReport = this.$store.getters["weeklyReports/weeklyReport"];
        this.openItemsModalVisible = false;
        this.$notify({
          type: "success",
          message: this.$t("WEEKLY_REPORTS.WEEKLY_REPORT_VALIDATED"),
        });
      } catch (error) {
        // Shows the business code (missing wages, inactive establishment) rather than a generic error.
        await this.$showRequestError(error);
      } finally {
        this.validating = false;
      }
    },

    async revertToDraft() {
      const confirmation = await swal.fire({
        title: this.$t("WEEKLY_REPORTS.CONFIRM_REVERT_TITLE"),
        text: this.$t("WEEKLY_REPORTS.CONFIRM_REVERT_TEXT"),
        type: "warning",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) return;

      try {
        await this.$store.dispatch("weeklyReports/revertToDraft", this.weeklyReport.id);
        this.weeklyReport = this.$store.getters["weeklyReports/weeklyReport"];
        this.$notify({
          type: "success",
          message: this.$t("WEEKLY_REPORTS.WEEKLY_REPORT_REVERTED"),
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewWeeklyReport(weeklyReport) {
      this.$router.push({
        name: "View WeeklyReport",
        params: { id: weeklyReport.id },
      });
    },
    onFormChanged() {
      this.$emit("formChanged");
    },
    goBack() {
      this.$router.push({ name: "List WeeklyReports" });
    },

    async updateWeeklyReport(updatedReport) {
      // Don't allow updates on validated reports
      if (this.weeklyReport.status === 'VALIDATED') return;

      // Immediately update local state for responsive UI
      this.weeklyReport = { ...this.weeklyReport, ...updatedReport };

      // Show pending changes indicator
      this.hasPendingChanges = true;

      // Cancel pending API call if user is still typing
      if (this.debouncedUpdateWeeklyReport.cancel) {
        this.debouncedUpdateWeeklyReport.cancel();
      }

      // Use longer debounced save to prevent excessive API calls
      this.debouncedUpdateWeeklyReport();
    },

    // Saves pending edits now instead of waiting for the debounce; false when the save failed.
    async flushPendingChanges() {
      if (!this.hasPendingChanges) {
        return true;
      }
      if (this.debouncedUpdateWeeklyReport && this.debouncedUpdateWeeklyReport.cancel) {
        this.debouncedUpdateWeeklyReport.cancel();
      }
      await this.saveWeeklyReport();
      return !this.hasPendingChanges;
    },

    performOptimisticUpdate(updatedReport) {
      // Immediate UI update without API call for better responsiveness
      this.weeklyReport = { ...this.weeklyReport, ...updatedReport };
      this.hasPendingChanges = true;
    },

    async saveWeeklyReport() {
      // Don't save if already updating
      if (this.updating) {
        return;
      }

      const weeklyReportData = cloneDeep(this.weeklyReport);
      this.updating = true;
      this.hasPendingChanges = false;

      try {
        await this.$store.dispatch("weeklyReports/update", weeklyReportData);
        this.weeklyReport = this.$store.getters["weeklyReports/weeklyReport"];

        this.$notify({
          type: "success",
          message: this.$t("WEEKLY_REPORTS.WEEKLY_REPORT_UPDATED"),
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.formErrors = error.response?.data?.errors;

        // Restore pending changes indicator on error
        this.hasPendingChanges = true;

        // Revert optimistic update on error
        await this.get();
      } finally {
        this.updating = false;
      }
    },

    formatPercentageDifference(value) {
      if (!value && value !== 0) return "0.00%";
      const prefix = parseFloat(value) > 0 ? "+" : "";
      return prefix + parseFloat(value).toFixed(2) + "%";
    },

    formatCurrencyDifference(value) {
      if (!value && value !== 0) return this.$formatCurrency(0);
      const prefix = parseFloat(value) > 0 ? "+" : "";
      return (
        prefix + this.$formatCurrency(parseFloat(Math.abs(value)).toFixed(2))
      );
    },

    getDifferenceClass(difference, isProfitMetric = false) {
      if (isProfitMetric) {
        if (difference > 0) return "text-success";
        if (difference < 0) return "text-danger";
        return "text-muted";
      }
      if (difference > 0) return "text-danger";
      if (difference < 0) return "text-success";
      return "text-muted";
    },

    getTargetStatusClass(difference, isProfitMetric = false) {
      // For costs (wages, purchases), exceeding target is bad
      // For profit, falling below target is bad
      if (isProfitMetric) {
        return difference >= 0 ? "badge-success" : "badge-danger";
      } else {
        return difference <= 0 ? "badge-success" : "badge-danger";
      }
    },

    getTargetStatusText(difference, isProfitMetric = false) {
      // For costs (wages, purchases), exceeding target is bad
      // For profit, falling below target is bad
      if (isProfitMetric) {
        return difference >= 0
          ? this.$t("WEEKLY_REPORTS.ABOVE_TARGET")
          : this.$t("WEEKLY_REPORTS.BELOW_TARGET");
      } else {
        return difference <= 0
          ? this.$t("WEEKLY_REPORTS.UNDER_TARGET")
          : this.$t("WEEKLY_REPORTS.OVER_TARGET");
      }
    },

    setupKeyboardShortcuts() {
      this.keyboardHandler = (event) => {
        // Ctrl+S or Cmd+S to manually save
        if ((event.ctrlKey || event.metaKey) && event.key === "s") {
          event.preventDefault();
          if (this.hasPendingChanges) {
            this.saveWeeklyReport();
          }
        }
      };

      document.addEventListener("keydown", this.keyboardHandler);
    },

    removeKeyboardShortcuts() {
      if (this.keyboardHandler) {
        document.removeEventListener("keydown", this.keyboardHandler);
      }
    },
  },

  watch: {
    formErrors(errors) {
      if (errors) {
        this.setApiValidation(errors);
      }
    },
  },
};
</script>

<style>
.card-stats .card-body {
  padding: 1rem 1.5rem;
}

.wr-kpi-value {
  cursor: help;
}

.wr-kpi-icon {
  font-size: 0.6em;
  color: #8898aa;
  vertical-align: middle;
}


.icon-shape {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-gradient-green {
  background: linear-gradient(87deg, #2dce89, #26a96c) !important;
}

.bg-gradient-danger {
  background: linear-gradient(87deg, #f5365c, #f56036) !important;
}

.bg-gradient-info {
  background: linear-gradient(87deg, #11cdef, #1171ef) !important;
}

/* Target Ratio Cards Styles */
.target-ratio-card {
  border: none;
  transition: transform 0.2s ease-in-out;
}

.target-ratio-card:hover {
  transform: translateY(-2px);
}

.target-ratio-card .card-header {
  border-bottom: none;
  font-weight: 600;
}

.target-ratio-card .card-body {
  font-size: 0.9rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.text-danger {
  color: #dc3545 !important;
}

.text-success {
  color: #28a745 !important;
}

.badge-success {
  color: #fff;
  background-color: #28a745;
}

.badge-danger {
  color: #fff;
  background-color: #dc3545;
}

.badge-warning {
  color: #212529;
  background-color: #ffc107;
}

/* Visual feedback for pending changes */
.has-pending-changes {
  position: relative;
}

.has-pending-changes::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 8px;
  background-color: #ffc107;
  border-radius: 50%;
  border: 2px solid white;
}

/* Input focus improvements */
.form-control:focus {
  border-color: #5e72e4;
  box-shadow: 0 0 0 0.2rem rgba(94, 114, 228, 0.25);
}

/* Typing indicator */
.typing-indicator {
  font-size: 0.75rem;
  color: #6c757d;
  font-style: italic;
}

/* Auto-save success animation */
@keyframes saveSuccess {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.save-success {
  animation: saveSuccess 0.3s ease-in-out;
}
</style>
